import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { adminApi, TOKEN_KEY, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [ready, setReady] = useState(false);

  const loadMe = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setUser(false); setReady(true); return; }
    try {
      const { data } = await adminApi.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const DEVICE_KEY = "hns_device";

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await adminApi.post("/auth/login", { email, password, device_token: localStorage.getItem(DEVICE_KEY) || null });
      if (data.requires_code) return { ok: false, requiresCode: true, challenge: data.challenge, emailHint: data.email_hint };
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, []);

  const verifyCode = useCallback(async (challenge, code, rememberDevice) => {
    try {
      const { data } = await adminApi.post("/auth/verify-code", { challenge, code, remember_device: rememberDevice });
      localStorage.setItem(TOKEN_KEY, data.token);
      if (data.device_token) localStorage.setItem(DEVICE_KEY, data.device_token);
      else localStorage.removeItem(DEVICE_KEY);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail), status: e.response?.status };
    }
  }, []);

  const resendCode = useCallback(async (challenge) => {
    try {
      const { data } = await adminApi.post("/auth/resend-code", { challenge });
      return { ok: true, challenge: data.challenge };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, []);

  const setTwoFA = useCallback(async (enabled, currentPassword) => {
    try {
      await adminApi.put("/auth/twofa", { enabled, current_password: currentPassword });
      if (!enabled) localStorage.removeItem(DEVICE_KEY);
      setUser((u) => (u ? { ...u, twofa_enabled: enabled } : u));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, []);

  const forgetDevices = useCallback(async () => {
    try {
      await adminApi.delete("/auth/trusted-devices");
      localStorage.removeItem(DEVICE_KEY);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, []);

  const isDeviceRemembered = useCallback(() => !!localStorage.getItem(DEVICE_KEY), []);
  const forgetThisDevice = useCallback(() => localStorage.removeItem(DEVICE_KEY), []);

  const listDevices = useCallback(async () => {
    const { data } = await adminApi.get("/auth/trusted-devices", { params: { device_token: localStorage.getItem(DEVICE_KEY) || undefined } });
    return data;
  }, []);

  const removeDevice = useCallback(async (d) => {
    try {
      await adminApi.delete(`/auth/trusted-devices/${d.id}`);
      if (d.current) localStorage.removeItem(DEVICE_KEY);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, []);

  const changePassword = useCallback(async (payload) => {
    try {
      const { data } = await adminApi.post("/auth/change-password", payload);
      if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
      await loadMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, [loadMe]);

  const changeEmail = useCallback(async (payload) => {
    try {
      const { data } = await adminApi.post("/auth/change-email", payload);
      if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
      await loadMe();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, [loadMe]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  }, []);

  const logoutEverywhere = useCallback(async () => {
    try { await adminApi.post("/auth/logout-everywhere"); } catch { /* ignore */ }
    logout();
  }, [logout]);

  return (
    <AuthContext.Provider value={{ user, ready, login, verifyCode, resendCode, setTwoFA, forgetDevices, isDeviceRemembered, forgetThisDevice, listDevices, removeDevice, logout, logoutEverywhere, changePassword, changeEmail }}>
      {children}
    </AuthContext.Provider>
  );
};
